import { ORDERED_LIST, UNORDERED_LIST } from '@lumapps/wrex-list/constants';
import { ListEditor } from '@lumapps/wrex-list/types';
import { Editor, ReactEditor } from '@lumapps/wrex/slate';
import { isElement } from '@lumapps/wrex/slate/utils/isElement';

import type { IsTextualBlock } from './createTextualBlockKeyHandler';

interface Options {
    /** Predicate matching the textual block element. */
    isBlock: IsTextualBlock;
    /** Predicate matching a block a textual block can be toggled from/to (usually `isParagraph`). */
    canToggleFrom: IsTextualBlock;
}

function haveListEnabled(editor: (Editor & ListEditor) | ReactEditor): editor is Editor & ListEditor {
    return 'isListActive' in editor;
}

/**
 * Whether a textual block can be toggled at the current selection: only when every selected block is an
 * allowed block (e.g. a paragraph) or an existing block of this type, and never inside a list.
 * Shared by `isCodeBlockAllowed`, `isInfoPanelAllowed`, …
 */
export const isTextualBlockToggleAllowed = (
    editor: (Editor & ListEditor) | ReactEditor,
    { isBlock, canToggleFrom }: Options,
): boolean => {
    // Editor cursor should be available.
    if (!editor.selection) {
        return true;
    }

    // If the editor also has list support, disallow inside a list.
    const isInList = haveListEnabled(editor)
        ? editor.isListActive(UNORDERED_LIST) || editor.isListActive(ORDERED_LIST)
        : false;

    const elements = Array.from(Editor.nodes(editor, { at: editor.selection, match: isElement, mode: 'lowest' }));

    return elements.every(([node]) => canToggleFrom(node) || isBlock(node)) && !isInList;
};
